import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import { useFilteredOrders } from '../../stores/orderItems';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';
import { toast } from 'react-hot-toast';
import { Order } from '../../stores/orderItems';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavPills from '../../components/NavPills/NavPills';

const styles = {
  scrollBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: 'calc(100vh - 400px)',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable',
    paddingRight: '6px',
    boxSizing: 'content-box',
    '&::-webkit-scrollbar': {
      width: '12px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '4px',
    },
  },
};

export default function OrderBubbleContent() {
  const { t } = useTranslation();
  const { filteredOrders, ongoingOrdersCount, isLoading } = useFilteredOrders();
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  const totalOrdersMessage = () => {
    if (ongoingOrdersCount === 0) {
      return t('orders.no_ongoing_orders');
    }
    return t('orders.total_ongoing_orders', { count: ongoingOrdersCount });
  };

  const updateOrderStatusMutation = useMutation({
    mutationFn: async ({ orderId, newStatus }: { orderId: number; newStatus: string }) => {
      const response = await axios.post(
        `${config.backend_base_url}d2rs/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success(t('orders.status_updated'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('common.error_occurred'));
    },
  });

  const handleStatusChange = (orderId: number, currentStatus: string) => {
    let newStatus;
    switch (currentStatus) {
      case 'unclaimed':
        newStatus = 'in_progress';
        break;
      case 'in_progress':
        newStatus = 'waiting_for_delivery';
        break;
      case 'waiting_for_delivery':
        newStatus = 'delivered';
        break;
      default:
        return;
    }

    updateOrderStatusMutation.mutate({ orderId, newStatus });
  };

  const renderOrderStatus = (status: string) => {
    switch (status) {
      case 'unclaimed':
        return <Typography sx={{ color: 'warning.main' }}>{t('orders.unclaimed')}</Typography>;
      case 'in_progress':
        return <Typography sx={{ color: 'info.main' }}>{t('orders.in_progress')}</Typography>;
      case 'waiting_for_delivery':
        return (
          <Typography sx={{ color: 'secondary.main' }}>
            {t('orders.waiting_for_delivery')}
          </Typography>
        );
      case 'delivered':
        return <Typography sx={{ color: 'success.main' }}>{t('orders.delivered')}</Typography>;
      default:
        return <Typography>{status}</Typography>;
    }
  };

  const renderActionButton = (order: Order) => {
    switch (order.status) {
      case 'unclaimed':
        return (
          <Tooltip title={t('orders.start_processing')}>
            <IconButton
              onClick={() => handleStatusChange(order.id, order.status)}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
        );
      case 'in_progress':
        return (
          <Tooltip title={t('orders.mark_ready_for_delivery')}>
            <IconButton
              onClick={() => handleStatusChange(order.id, order.status)}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <LocalShippingIcon />
            </IconButton>
          </Tooltip>
        );
      case 'waiting_for_delivery':
        return (
          <Tooltip title={t('orders.mark_delivered')}>
            <IconButton
              onClick={() => handleStatusChange(order.id, order.status)}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <DoneIcon />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const renderOrderList = (orders: Order[]) => {
    if (orders.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1">{t('orders.no_orders_found')}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={styles.scrollBox}>
        {orders.map((order) => (
          <Box
            key={order.id}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
              position: 'relative',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                #{order.id}: {order.title}
              </Typography>
              {renderActionButton(order)}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('orders.status')}:
                </Typography>
                {renderOrderStatus(order.status)}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('orders.customer')}:
                </Typography>
                <Typography variant="body2">
                  {order.customer_name} {order.customer_lastname}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('orders.created_at')}:
                </Typography>
                <Typography variant="body2">
                  {new Date(order.created_at).toLocaleString()}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('orders.d2rs_order_id')}:
                </Typography>
                <Typography variant="body2">{order.d2rs_order_id}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography
          variant="subtitle1"
          color={ongoingOrdersCount > 0 ? 'primary' : 'textSecondary'}
        >
          {totalOrdersMessage()}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <NavPills
            queryKey="status"
            alignCenter
            twoTabsInRow
            color="primary"
            skipUrlUpdate={true}
            tabs={[
              {
                tabName: 'unclaimed',
                tabButton: `${t('orders.unclaimed')} (${filteredOrders.unclaimed.length})`,
                tabContent: renderOrderList(filteredOrders.unclaimed),
              },
              {
                tabName: 'in_progress',
                tabButton: `${t('orders.in_progress')} (${filteredOrders.inProgress.length})`,
                tabContent: renderOrderList(filteredOrders.inProgress),
              },
              {
                tabName: 'waiting_for_delivery',
                tabButton: `${t('orders.waiting_for_delivery')} (${filteredOrders.waitingForDelivery.length})`,
                tabContent: renderOrderList(filteredOrders.waitingForDelivery),
              },
              {
                tabName: 'delivered',
                tabButton: `${t('orders.delivered')} (${filteredOrders.delivered.length})`,
                tabContent: renderOrderList(filteredOrders.delivered),
              },
            ]}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          href="/orders"
          endIcon={<OpenInNewIcon />}
        >
          {t('orders.view_all_orders')}
        </Button>
      </Box>
    </Box>
  );
}
